import { z } from "zod";

import { unlistedEnumValueSchema } from "./enum-read.schema";

/**
 * The READ side of a server discriminated union — the same shape the audit
 * feed's unlisted-event arm has, for any union whose discriminant the server
 * controls.
 *
 * A reader composes it as
 *
 *   z.union([Listed, z.looseObject({ kind: unlistedDiscriminantSchema(Listed) })])
 *
 * so a variant this build has a branch for parses as itself, and a variant a
 * newer backend added parses as a loose object carrying the server's own
 * discriminant — branded, so it cannot be mistaken for a listed one — and every
 * field it sent. The response it sits in survives; one outcome renders as "newer
 * than this client" instead of the whole read failing.
 *
 * The unlisted arm sits OUTSIDE the discriminated union on purpose: a member
 * whose discriminator is not a literal disables literal narrowing for every
 * member. Readers ask {@link isListedVariant} first and switch on the listed half.
 *
 * A LISTED discriminant whose fields do not fit its branch still fails the
 * parse: the unlisted half refuses every listed value, so a broken payload is
 * never silently re-read as a newer one.
 *
 * The emitting side never uses this: a backend sending a variant it does not
 * list is a bug, and the strict union is what reports it.
 */

/** Every discriminant value `listed` has a branch for, read off the union itself. */
export function listedDiscriminantValues(listed: z.core.$ZodDiscriminatedUnion): readonly string[] {
  const values = listed._zod.propValues[listed._zod.def.discriminator] ?? new Set();
  return [...values].filter((value): value is string => typeof value === "string");
}

/** A discriminant `listed` has no branch for, branded as unlisted. */
export function unlistedDiscriminantSchema(listed: z.core.$ZodDiscriminatedUnion) {
  return unlistedEnumValueSchema(listedDiscriminantValues(listed));
}

/**
 * True for a read value that `listed` has a branch for. Narrows the read union to
 * the listed half, which a reader can then switch on exhaustively.
 */
export function isListedVariant<L extends z.core.$ZodDiscriminatedUnion, V extends object>(
  listed: L,
  value: V
): value is Extract<V, z.output<L>> {
  const discriminant: unknown = Object.entries(value).find(
    ([key]) => key === listed._zod.def.discriminator
  )?.[1];
  return (
    typeof discriminant === "string" && listedDiscriminantValues(listed).includes(discriminant)
  );
}
